import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardBody } from '@/components/ui/Card';
import { Package, Loader2 } from 'lucide-react';

export function Register() {
  const { token } = useParams<{ token: string }>();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [invitation, setInvitation] = useState<{ email: string; role: string } | null>(null);

  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      api.get(`/auth/invitation/${token}`).then((res) => {
        if (res.success) {
          setInvitation(res.data as { email: string; role: string });
        } else {
          setError(res.error || 'Invalid invitation');
        }
        setValidating(false);
      });
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await register(token!, name, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-sm w-full">
          <CardBody className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-error/20 flex items-center justify-center mb-4">
              <Package className="h-8 w-8 text-error" />
            </div>
            <h2 className="text-lg font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-foreground-muted mb-4">{error || 'This invitation link is invalid or has expired.'}</p>
            <Button variant="secondary" onClick={() => navigate('/login')}>
              Back to Login
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary mb-4">
            <Package className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold">Create Account</h1>
          <p className="text-foreground-muted mt-1">Complete your registration</p>
        </div>

        <Card>
          <CardBody>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 text-sm text-error bg-error/10 rounded-lg">
                  {error}
                </div>
              )}

              <div className="p-3 bg-surface-hover rounded-lg">
                <p className="text-sm text-foreground-muted">Registering as:</p>
                <p className="font-medium">{invitation.email}</p>
              </div>

              <Input
                type="text"
                label="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
                autoComplete="name"
              />

              <Input
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
                autoComplete="new-password"
                hint="At least 8 characters with uppercase, lowercase, and number"
              />

              <Input
                type="password"
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                autoComplete="new-password"
              />

              <Button type="submit" className="w-full" loading={loading}>
                Create Account
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
